








































































































































































import { computed, defineComponent, provide, reactive, ref } from "@vue/composition-api";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { ContentFeed } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import createDeferred from "@/lib/deferredPromise";
import emitter from "@/lib/emitter";
import { formatMoneyString, getCostByCurrency, getMainCurrencyCode } from "@/lib/moneyHelpers";
import { timeAgo } from "@/lib/time";
import { getAbsUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { StoryFeed, StoryPublisherWithStories } from "@/types";
import { EVENTS } from "@/const";
import BaseAvatar from "@/components/BaseAvatar";
import BaseIcon from "@/components/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import BaseSpinner from "@/components/BaseSpinner";
import ContentFeedItemMenu from "@/components/ContentFeedItem/components/ContentFeedItemMenu";
import PostTipping from "@/components/PostTipping";
import StoriesModal from "@/components/StoriesModal";
import { StoryStatisticType } from "@/components/StoriesStatistic/StoriesStatistic.vue";
import ScreenControls from "../ScreenControls";
import StoryDeleteConfirm from "../StoryDeleteConfirm";
import StoryItemMedia from "../StoryItemMedia";
import StoryReaction from "../StoryReaction";
import StorySubscribeView from "../StorySubscribeView";
import StoryTimerView from "../StoryTimerView";

export default defineComponent({
  name: "StoriesView",
  components: {
    BaseSpinner,
    StoryDeleteConfirm,
    StoriesModal,
    PostTipping,
    StorySubscribeView,
    StoryReaction,
    BaseIconButton,
    StoryTimerView,
    ContentFeedItemMenu,
    BaseIcon,
    StoryItemMedia,
    BaseAvatar,
    ScreenControls,
  },
  props: {
    loading: { type: Boolean },
    stories: { type: Array as () => StoryPublisherWithStories[], required: true },
    subscriptionLevels: { type: Array as () => SubscriptionLevel[], required: true },
    currentUserIndex: { type: Number, default: 0 },
    currentStoryIndex: { type: Number, default: 0 },
    followingsIds: { type: Array as () => string[], required: true },
  },
  setup(props) {
    const { authUser } = useAuthUser();
    const storySourceRef = ref<HTMLDivElement[]>();
    const indexSelected = ref(props.currentUserIndex);
    const indexForPosition = ref(props.currentUserIndex);
    const difference = computed(() => -indexForPosition.value);
    const key = ref(props.currentStoryIndex);
    const providedData: { user: UserPublicData | null } = reactive({ user: null });
    provide("messageData", providedData);
    const setShowTip = useAuthorisedOnly((item: ContentFeed) => {
      providedData.user = item.user;
    });

    return { authUser, storySourceRef, indexSelected, indexForPosition, difference, key, providedData, setShowTip };
  },
  data: () => ({
    percent: 0,
    timer: 0,
    progress: null as ReturnType<typeof setInterval> | null,
    interval: null as ReturnType<typeof setInterval> | null,
    isPaused: true,
    isMuted: false,
    newDur: 0,
    pausePer: 0,
    showDeleteConfirm: false,
    processingDelete: false,
    storyIdForDelete: null as string | null,
    storySourceWidth: 0,
    StoryStatisticType,
  }),
  computed: {
    isAllStoriesEnd(): boolean {
      return this.indexSelected >= this.stories.length - 1 && this.isCurrentAllImagesEnd;
    },
    isCurrentAllImagesEnd(): boolean {
      return this.key >= this.stories[this.indexSelected].feeds.length - 1;
    },
    isLastImageStart(): boolean {
      return this.key >= this.stories[this.indexSelected].feeds.length - 2;
    },
    currentMediaItem(): StoryFeed {
      return this.getMediaItem(this.stories[this.indexSelected], this.key);
    },
    isAuthor(): boolean {
      return this.currentMediaItem.userId === this.authUser?.id;
    },
    hasAccess(): boolean {
      return (
        this.currentMediaItem.purchased && (this.isAuthor || this.followingsIds.includes(this.currentMediaItem.userId))
      );
    },
    currentMediaDuration(): number {
      return this.hasAccess ? this.currentMediaItem.files[0].duration || 15000 : 15000;
    },
    currentPublishTime(): string {
      return this.currentMediaItem.publishTime ? timeAgo(this.currentMediaItem.publishTime, { addSuffix: true }) : "";
    },
    currentAccessInfo(): string | null {
      if (!this.isAuthor) return null;
      if (this.currentMediaItem.subscriptionLevelId === "0") return this.$t("Followers and subscribers") as string;

      const level = this.subscriptionLevels.find(level => level.id === this.currentMediaItem.subscriptionLevelId);
      const cost = level?.pricePackage ? getCostByCurrency(level.pricePackage) || null : null;

      if (!cost) return null;
      const price = formatMoneyString(cost.cost, {
        currencyCode: cost.currency,
        forceNumberSymbol: false,
      });

      return this.$t("{0} subscribers", [price]) as string;
    },
    shareLink(): string {
      return getAbsUrl(`/${this.stories[this.indexSelected].user.username}?storyId=${this.currentMediaItem.id}`);
    },
    canTip(): boolean {
      return config.featureTippingPosts && !this.isAuthor;
    },
    showTip(): boolean {
      return !!this.providedData.user && config.featureTippingPosts;
    },
  },
  methods: {
    formatTips(amount: number) {
      return formatMoneyString(amount, { currencyCode: getMainCurrencyCode() });
    },
    async updateIndexSelected(newIndex: number, key: number = -1) {
      const { promise, resolve, reject } = createDeferred();
      this.indexForPosition = newIndex;
      if (this.indexSelected !== newIndex) {
        this.$emit("changeUser", newIndex, async (hasStory: boolean) => {
          if (hasStory) resolve();
          else reject();
        });
        await promise
          .then(() => {
            this.indexSelected = newIndex;
            this.key = key > -1 ? key : this.getLastViewedIndex(this.stories[this.indexSelected]);
          })
          .catch(async () => {
            const checkIndex = newIndex + (newIndex > this.indexSelected ? 1 : -1);
            if (checkIndex === this.stories.length) this.onAllStoriesEnd();
            else await this.updateIndexSelected(checkIndex < 0 ? 1 : checkIndex, key);
          });
      }
    },
    onSubscribe(feed: StoryFeed) {
      this.pauseStory(feed.id);
      emitter.emit(EVENTS.CONTENT_FEED_PURCHASE, feed);
    },
    getMediaItem(story: StoryPublisherWithStories, index: number) {
      return story.feeds[index];
    },
    getSrc(story: StoryPublisherWithStories, index: number) {
      const viewedIndex = this.getLastViewedIndex(story);
      return index === this.indexSelected ? this.currentMediaItem : this.getMediaItem(story, viewedIndex);
    },
    getNotViewedIndex(story: StoryPublisherWithStories) {
      return story.feeds.findIndex(item => !item.viewed);
    },
    getLastViewedIndex(story: StoryPublisherWithStories) {
      const keyIndex = this.getNotViewedIndex(story);
      const index = story.feeds.length - 1;
      return keyIndex === -1 ? index : keyIndex;
    },
    async selectSlide(index: number, key: number = -1) {
      this.stopVideo(this.currentMediaItem.id);
      await this.updateIndexSelected(index, key);
    },
    onAllStoriesEnd() {
      this.indexSelected = 0;
      this.indexForPosition = 0;
      this.key = 0;
      this.$emit("allStoriesEnd");
    },
    async onCurrentAllImagesEnd(index: number) {
      this.stopVideo(this.currentMediaItem.id);
      this.stories[index].feeds[this.key].viewed = true;
      await this.updateIndexSelected(this.indexSelected + 1);
      this.$emit("currentAllImagesEnd", index);
    },
    onCurrentImageEnd(index: number) {
      this.stories[index].feeds[this.key].viewed = true;
      this.$emit("currentImageEnd", this.key);
      this.key++;
    },
    next(index: number) {
      if (this.isLastImageStart) {
        this.$emit("getNextPage");
      }
      if (this.isAllStoriesEnd) {
        this.onAllStoriesEnd();
      } else if (this.isCurrentAllImagesEnd) {
        setTimeout(() => {
          this.onCurrentAllImagesEnd(index);
        });
      } else {
        this.currentMediaItem.viewed = true;
        this.key++;
      }
      this.reset();
    },
    async prev() {
      if (this.indexSelected <= 0 && this.key <= 0) {
        this.key = 0;
        await this.selectSlide(this.indexSelected, this.key);
      } else if (this.key <= 0) {
        setTimeout(async () => {
          await this.updateIndexSelected(this.indexSelected - 1);
        });
      } else {
        this.key--;
        this.currentMediaItem.viewed = false;
      }
      this.reset();
    },
    autoPlay() {
      if (this.isLastImageStart) {
        this.$emit("getNextPage");
      }
      if (this.isAllStoriesEnd) {
        this.onAllStoriesEnd();
      } else if (this.isCurrentAllImagesEnd) {
        this.onCurrentAllImagesEnd(this.indexSelected);
      } else {
        this.onCurrentImageEnd(this.indexSelected);
      }
      this.reset();
    },
    play() {
      this.timer = new Date().getTime();
      this.progress = setInterval(() => {
        let time = new Date().getTime();
        if (this.newDur > 0) {
          this.percent = this.pausePer + Math.floor((100 * (time - this.timer)) / this.currentMediaDuration);
        } else {
          this.percent = Math.floor((100 * (time - this.timer)) / this.currentMediaDuration);
        }
      }, this.currentMediaDuration / 100);
      if (this.newDur > 0) {
        this.interval = setInterval(this.autoPlay, this.newDur);
      } else {
        this.interval = setInterval(this.autoPlay, this.currentMediaDuration);
      }
    },
    reset() {
      this.percent = 0;
      this.interval && clearInterval(this.interval);
      this.progress && clearInterval(this.progress);
      this.newDur = 0;
    },
    toggleStoryPlaying(id: string | undefined) {
      this.isPaused ? this.playStory(id) : this.pauseStory(id);
    },
    pauseStory(id: string | undefined) {
      if (id) {
        this.toggleVideo("pause", id);
      }
      this.isPaused = true;
      this.pausePer = this.percent;
      this.progress && clearInterval(this.progress);
      this.interval && clearInterval(this.interval);
      this.newDur = this.currentMediaDuration - (this.pausePer * this.currentMediaDuration) / 100;
    },
    playStory(id: string | undefined) {
      if (id) {
        this.toggleVideo("play", id);
      }
      this.isPaused = false;
      this.play();
    },
    toggleVideo(type: "play" | "pause", id: string) {
      const video = document.querySelector(`video[id="${id}"]`) as HTMLVideoElement | null;
      if (video) {
        if (type === "play")
          video.play().catch(async () => {
            this.isMuted = true;
            await this.$nextTick();
            video.play().catch(() => this.pauseStory(this.currentMediaItem.id));
          });
        else video.pause();
      }
    },
    stopVideo(id: string) {
      const video = document.querySelector(`video[id="${id}"]`) as HTMLVideoElement | null;
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    },
    calculateTransform(index: number) {
      if (this.storySourceRef) {
        const margin = 32;
        const storyBlockWidth = 104;
        const numberSign = index - this.indexForPosition < 0 ? -1 : 1;
        const prevOrNext = this.indexForPosition - index === -1 || this.indexForPosition - index === 1;

        return (
          "calc(-50% + " +
          (numberSign * this.storySourceWidth) / 2 +
          "px + " +
          (numberSign * storyBlockWidth) / 2 +
          "px + " +
          (prevOrNext ? "" : (index - this.indexForPosition - numberSign) * storyBlockWidth + "px + ") +
          numberSign * margin +
          "px)"
        );
      }
      return "-50%";
    },
    closeStories() {
      this.$emit("closeStories");
    },
    async onDelete() {
      try {
        this.processingDelete = true;
        if (this.storyIdForDelete && (await providers.ContentSetProvider.delete({ id: this.storyIdForDelete }))) {
          await this.prev();
          this.$emit("delete", this.storyIdForDelete);
        }
      } finally {
        this.storyIdForDelete = null;
        this.processingDelete = false;
      }
    },
    onMediaCanPlay() {
      if (this.isPaused) {
        this.playStory(this.currentMediaItem.id);
      }
    },
    initIndexAndStart() {
      this.selectSlide(this.currentUserIndex, this.currentStoryIndex);
    },
    handleTip(item: ContentFeed) {
      this.pauseStory(item.id);
      this.setShowTip(item);
    },
    closeTip() {
      this.providedData.user = null;
      this.playStory(this.currentMediaItem.id);
    },
    setStoryView() {
      if (!this.storySourceRef) return;
      this.storySourceWidth = this.storySourceRef[this.indexForPosition].clientWidth;
    },
    onStatisticClick(tab: StoryStatisticType) {
      this.$emit("showStatistic", tab);
      this.pauseStory(this.currentMediaItem.id);
    },
  },
  watch: {
    currentMediaItem: {
      async handler() {
        this.isPaused = true;
        this.reset();
        this.$emit("startViewStory", this.currentMediaItem.id);
      },
      immediate: true,
    },
  },
  created() {
    this.initIndexAndStart();
    window.addEventListener("resize", this.setStoryView);
  },
  mounted() {
    this.setStoryView();
  },
  beforeDestroy() {
    this.progress && clearInterval(this.progress);
    this.interval && clearInterval(this.interval);
    window.removeEventListener("resize", this.setStoryView);
  },
});
